/*---------------------------------------------------------------------------------------------
 * Copyright (c) Bentley Systems, Incorporated. All rights reserved.
 * See LICENSE.md in the project root for license terms and full copyright notice.
 *--------------------------------------------------------------------------------------------*/
import * as React from 'react';
import cx from 'classnames';
import { Button } from '../Button/index.js';
import { DropdownMenu } from '../../DropdownMenu/index.js';
import { SvgCaretDownSmall, SvgCaretUpSmall, useTheme, useMergedRefs, } from '../../utils/index.js';
import '@itwin/itwinui-css/css/button.css';
/**
 * Button that opens a DropdownMenu.
 * @example
 * const menuItems = (close: () => void) => [
 *   <MenuItem key={1} onClick={onClick(1, close)}>Item #1</MenuItem>,
 *   <MenuItem key={2} onClick={onClick(2, close)}>Item #2</MenuItem>,
 * ];
 * <DropdownButton menuItems={menuItems}>Default</DropdownButton>
 */
export const DropdownButton = React.forwardRef((props, ref) => {
    const { menuItems, className, size, styleType, children, dropdownMenuProps, ...rest } = props;
    useTheme();
    const [isMenuOpen, setIsMenuOpen] = React.useState(false);
    const [menuWidth, setMenuWidth] = React.useState(0);
    const buttonRef = React.useRef(null);
    const refs = useMergedRefs(ref, buttonRef);
    React.useEffect(() => {
        if (buttonRef.current) {
            setMenuWidth(buttonRef.current.offsetWidth);
        }
    }, [children, size, styleType]);
    return (React.createElement(DropdownMenu, { menuItems: menuItems, ...dropdownMenuProps, onShow: (i) => {
            var _a;
            setIsMenuOpen(true);
            (_a = dropdownMenuProps === null || dropdownMenuProps === void 0 ? void 0 : dropdownMenuProps.onShow) === null || _a === void 0 ? void 0 : _a.call(dropdownMenuProps, i);
        }, onHide: (i) => {
            var _a;
            setIsMenuOpen(false);
            (_a = dropdownMenuProps === null || dropdownMenuProps === void 0 ? void 0 : dropdownMenuProps.onHide) === null || _a === void 0 ? void 0 : _a.call(dropdownMenuProps, i);
        }, style: { minWidth: menuWidth, ...dropdownMenuProps === null || dropdownMenuProps === void 0 ? void 0 : dropdownMenuProps.style } },
        React.createElement(Button, { className: cx('iui-button-dropdown', className), size: size, styleType: styleType, endIcon: isMenuOpen ? (React.createElement(SvgCaretUpSmall, { "aria-hidden": true })) : (React.createElement(SvgCaretDownSmall, { "aria-hidden": true })), ref: refs, "aria-label": 'Dropdown', ...rest }, children)));
});
export default DropdownButton;
